/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import { LogActionId } from "@smartsheet/logging-state-service";
import { useMemo } from "react";
import { useLoggingDecorator, useLoggingEnabledByDefault } from "../../util/logging";
import { SelectBase } from "./SelectBase";
import { LayoutContextProvider } from "./components/LayoutContext";
import { DEFAULT_ALL_OPTION_VALUE, MultiSelectOption } from "./components/MultiSelectOption";
import { MultiValueContainer } from "./components/MultiValueContainer";
import { SelectAllContext } from "./components/SelectAllContext";
import { DEFAULT_ITEM_HEIGHT, MAX_OPTIONS_UNTIL_VIRTUALIZATION } from "./selectConstants";
const getComponents = function () {
  let components = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return {
    Option: components.MultiSelectOption || MultiSelectOption,
    ValueContainer: components.MultiValueContainer || MultiValueContainer,
    ...components
  };
};
const hasSelectAllConfig = config => config !== null && typeof config === "object";

/**
 * Wraps the SelectBase component with correct props for a "Multi" select (versus the blend of
 * Single- and Multi- select props that SelectBase takes).
 *
 * Also provides some default implementations for multi-select behavior (such as a select-all
 * option and checkboxes next to options).
 *
 * @deprecated This component is being removed in V6+, February 2025, please use `MultiSelectV2` instead.
 */
export const MultiSelect = _ref => {
  let {
    validationState = "default",
    closeMenuOnSelect = false,
    maxOptionsUntilVirtualization = MAX_OPTIONS_UNTIL_VIRTUALIZATION,
    optionHeight = DEFAULT_ITEM_HEIGHT,
    closeMenuOnScroll = false,
    createOptionPosition = "last",
    isClearable = true,
    isSearchable = true,
    menuPortalTarget = document.body,
    menuPlacement = "auto",
    selectAllConfig = false,
    components,
    ...restProps
  } = _ref;
  const {
    // Setting defaults on these outside of props destructuring to keep them marked as required for mdx
    value = [],
    options = [],
    onChange = () => {}
  } = restProps;
  const withLogging = useLoggingDecorator({
    controlType: "mlc",
    clientId: restProps.clientId
  });
  const loggableChangeHandler = useLoggingEnabledByDefault(withLogging, {
    actionId: LogActionId.CLICK
  }, onChange);
  const handleChange = (selected, actionMeta) => {
    if (selected && selected.length > 0 &&
    // Might seem weird since select all is the first option in the list, but the selected items are returned
    // in the order they're selected in.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    selected[selected.length - 1].value === DEFAULT_ALL_OPTION_VALUE) {
      // Select All option was selected
      if (value?.length === options.length) {
        // Every option is selected already, so we should un-select everything
        return loggableChangeHandler([], {
          action: "clear"
        });
      } else {
        // Select all the options
        return loggableChangeHandler(options, actionMeta);
      }
    }
    return loggableChangeHandler(selected, actionMeta);
  };
  const hasSelectAll = hasSelectAllConfig(selectAllConfig);
  const memoizedOptions = useMemo(() => {
    const optionsCopy = [...(hasSelectAll ? [{
      label: "",
      value: DEFAULT_ALL_OPTION_VALUE
    }] : []), ...options];
    return optionsCopy;
  }, [options, hasSelectAll]);
  let newProps = {
    // Consumer can override these props.
    closeMenuOnSelect,
    createOptionPosition,
    validationState,
    maxOptionsUntilVirtualization,
    closeMenuOnScroll,
    isClearable,
    isSearchable,
    optionHeight,
    menuPortalTarget,
    menuPlacement,
    ...restProps,
    // These *must* be after the restProps spread, since we don't want to let the consumer override them
    options: memoizedOptions,
    value: value,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    onChange: (value, action) => loggableChangeHandler(value, action),
    components: getComponents(
    // Once we re-align the components interfaces this cast won't be necessary :(
    // See https://git.lab.smartsheet.com/design-systems-team/lodestar-core/-/issues/249
    components),
    hideSelectedOptions: false,
    isMulti: true
  };
  if (hasSelectAll) {
    // Amend new props to turn on Select All
    newProps = {
      ...newProps,
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onChange: (value, actionMeta) => handleChange(value, actionMeta)
    };
  }
  const getText = selectAllConfig && selectAllConfig.getText;
  const selectAllContextValue = useMemo(() => ({
    enabled: hasSelectAll,
    getText: getText || (() => "")
  }), [hasSelectAll, getText]);
  return jsx(LayoutContextProvider, null, jsx(SelectAllContext.Provider, {
    value: selectAllContextValue
  }, jsx(SelectBase, newProps)));
};
//# sourceMappingURL=MultiSelect.js.map